import Axios from 'axios'

const state = {
  devices: [],
  device_detail: {},
  deviceOwner: null,
  fcmTokens: [],
  loading: false,
  error: null,
  message: null,
  host: process.env.VUE_APP_API_HOST || 'http://localhost:8020',
  pagination: {
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false
  }
}

const getters = {
  getAllDevices: state => state.devices || [],
  getDevices: state => state.devices || [], // Add alias for backward compatibility
  getDeviceDetail: state => state.device_detail,
  getDeviceOwner: state => state.deviceOwner,
  getFcmTokens: state => state.fcmTokens || [],
  isLoading: state => state.loading,
  getError: state => state.error,
  getMessage: state => state.message,
  getPagination: state => state.pagination,
  getCurrentPage: state => state.pagination.currentPage,
  getTotalElements: state => state.pagination.totalElements
}

const mutations = {
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_DEVICES(state, devices) {
    state.devices = devices
  },
  SET_DEVICE_DETAIL(state, device) {
    state.device_detail = device
  },
  SET_DEVICE_OWNER(state, owner) {
    state.deviceOwner = owner
  },
  SET_FCM_TOKENS(state, tokens) {
    state.fcmTokens = tokens
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_MESSAGE(state, message) {
    state.message = message
  },
  SET_PAGINATION(state, paginationData) {
    state.pagination = {
      currentPage: paginationData.number || 0,
      totalPages: paginationData.totalPages || 0,
      totalElements: paginationData.totalElements || 0,
      pageSize: paginationData.size || 10,
      hasNext: !paginationData.last,
      hasPrevious: !paginationData.first
    }
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
  CLEAR_MESSAGE(state) {
    state.message = null
  },
  ADD_DEVICE(state, device) {
    state.devices.unshift(device)
  },
  UPDATE_DEVICE(state, updatedDevice) {
    const index = state.devices.findIndex(device => device.id === updatedDevice.id)
    if (index !== -1) {
      state.devices.splice(index, 1, updatedDevice)
    }
  },
  DELETE_DEVICE(state, deviceId) {
    state.devices = state.devices.filter(device => device.id !== deviceId)
  }
}

const actions = {
  async manageDevice({ commit, rootState }, deviceData) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      commit('CLEAR_MESSAGE')
      
      // Map frontend fields to backend expected fields
      const requestData = {
        username: deviceData.username,
        idDevice: deviceData.fcmToken || deviceData.idDevice || deviceData.deviceToken, // Map fcmToken to idDevice
        action: deviceData.action,
        deviceName: deviceData.deviceName,
        deviceType: deviceData.deviceType
      }
      
      console.log('Sending manageDevice request:', requestData)
      
      const response = await Axios.post(`${state.host}/api/devices/manage`, requestData, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`,
          'Content-Type': 'application/json'
        }
      })
      
      commit('SET_MESSAGE', response.data.message)
      return response.data
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Gagal mengelola device'
      console.error('Error in manageDevice:', error.response?.data)
      commit('SET_ERROR', errorMsg)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getUserDevices({ commit, rootState }, username) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/devices/user/${username}`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      commit('SET_DEVICES', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Gagal memuat devices')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getDeviceOwner({ commit, rootState }, deviceToken) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/devices/owner/${deviceToken}`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      commit('SET_DEVICE_OWNER', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Gagal memuat device owner')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateLastUsed({ commit, rootState }, deviceToken) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.post(`${state.host}/api/devices/update-last-used/${deviceToken}`, {}, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      commit('SET_MESSAGE', response.data.message)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Gagal update last used time')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async cleanupStaleDevices({ commit, rootState }, daysThreshold = 30) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.post(`${state.host}/api/devices/cleanup-stale`, {}, {
        params: { daysThreshold },
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      commit('SET_MESSAGE', response.data.message)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Gagal membersihkan stale devices')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getAllDevices({ commit, rootState }, { page = 0, size = 10, filters = {} } = {}) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      // Build query parameters
      const params = { page, size }
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params[key] = value
        }
      })
      
      const response = await Axios.get(`${state.host}/api/devices/all`, {
        params,
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      commit('SET_DEVICES', response.data.content || response.data)
      if (response.data.pageable || response.data.totalPages !== undefined) {
        commit('SET_PAGINATION', response.data)
      }
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Gagal memuat semua devices')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getFCMTokensByUsername({ commit, rootState }, username) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await Axios.get(`${state.host}/api/devices/fcm-tokens/${username}`, {
        headers: {
          'Authorization': `Bearer ${rootState.token}`
        }
      })
      
      commit('SET_FCM_TOKENS', response.data.tokens)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Gagal memuat FCM tokens')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },

  clearMessage({ commit }) {
    commit('CLEAR_MESSAGE')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
